import React, {useState} from 'react';
import {useDispatch} from 'react-redux';

import BackButton from '@/components/BackButton/BackButton';
import CloseButton from '@/components/CloseButton/CloseButton';
import {StickyPageLayout} from '@/components/Layout';
import {showConnectionCancellationModalAction} from '@/state/slices/modalSlice';
import {twx} from '@/utils/tailwindUtils';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

import ConnectionGuideContent from './ConnectionGuideContent';

const ConnectionGuidePageMobile = ({deviceType, contentItems, currentStep, content, onNextStep, useCloseButton}) => {
    const [isAnimating, setIsAnimating] = useState(false);

    const dispatch = useDispatch();
    const {actionButton, closeButton, images, videos, bgColorHex} = content;
    const handleClick = () => {
        dispatch(showConnectionCancellationModalAction());
    };
    const handleNextStep = () => {
        if (isAnimating) return;

        const isLastStep = currentStep >= contentItems.length - 1;

        if (isLastStep) {
            onNextStep();
            return;
        }

        setIsAnimating(true);
        setTimeout(() => {
            onNextStep();
            setIsAnimating(false);
        }, 700);
    };

    return (
        <StickyPageLayout
            className='items-center'
            headerOptions={{
                endIcon: useCloseButton ? <CloseButton onClick={handleClick} /> : null,
                startIcon: useCloseButton ? null : <BackButton onClick={handleClick} />,
            }}
            contentClassName='flex flex-1 flex-col items-center'
            footer={
                <ConnectionGuideContent.Buttons
                    actionButton={actionButton}
                    closeButton={closeButton}
                    onNextStep={handleNextStep}
                />
            }
        >
            <WarningBannersTop source={WARNING_BANNER_SOURCE_TYPES.CONNECTION} />
            <ConnectionGuideContent.Media
                className='h-[392px] w-full'
                bgColorHex={bgColorHex}
                images={images}
                videos={videos}
            />
            <div className='w-full flex-1 py-8'>
                <div className='relative flex h-full flex-col items-center' role='region' aria-live='polite'>
                    {contentItems.map((contentItem, index) => (
                        <AnimatedContent
                            key={index}
                            index={index}
                            currentStep={currentStep}
                            title={contentItem.title}
                            description={contentItem.description}
                            items={contentItem.items}
                            deviceType={deviceType}
                            isAnimating={isAnimating}
                        />
                    ))}
                </div>
            </div>
            <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.CONNECTION} />
        </StickyPageLayout>
    );
};

const AnimatedContent = ({index, currentStep, title, description, items, deviceType, isAnimating}) => {
    return (
        <div
            className={twx(
                'top-0 flex w-full flex-col items-center px-4 transition-[transform] duration-700 ease-in-out',
                index === currentStep ? 'relative' : 'absolute translate-x-full',
                index === currentStep
                    ? isAnimating
                        ? '-translate-x-full'
                        : ''
                    : index === currentStep + 1 && isAnimating
                      ? 'translate-x-0'
                      : 'translate-x-full',
                index < currentStep && 'hidden'
            )}
            tabIndex={index === currentStep ? 0 : -1}
            aria-hidden={index !== currentStep}
        >
            <ConnectionGuideContent.Title className='w-full' title={title} deviceType={deviceType} />
            <ConnectionGuideContent.Description description={description} deviceType={deviceType} />
            <ConnectionGuideContent.Instruction className='w-auto' items={items} deviceType={deviceType} />
        </div>
    );
};

export default ConnectionGuidePageMobile;
